import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { isTesting, macroCondition } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismCard } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import HotelBenchmarkInsightCompetitorsCompetitorsListContainer from 'lumen/components/hotel/benchmark-insight/competitors/competitors-list-container';
import HotelBenchmarkInsightCompetitorsMapCompetitorsMap from 'lumen/components/hotel/benchmark-insight/competitors/map/competitors-map';
import type {
  BenchmarkInsightCompetitorsCompetitor,
  BeniSmartCompset,
} from 'lumen/models';
import {
  BENI_COMPETITORS_TABS,
  type CompetitorsBenchmarkTab,
} from 'lumen/utils/constants/benchmark-insight/beni-constant-values';

import styles from './competitors-panel.module.css';

interface HotelBenchmarkInsightCompetitorsCompetitorsPanelSignature {
  Args: {
    activeTab: CompetitorsBenchmarkTab;
    compset: BenchmarkInsightCompetitorsCompetitor[];
    isLoading: boolean;
    setActiveTab: (id: CompetitorsBenchmarkTab) => void;
    smartCompset: BeniSmartCompset | null;
  };
  Blocks: {
    default: [];
  };
}

export default class HotelBenchmarkInsightCompetitorsCompetitorsPanel extends Component<HotelBenchmarkInsightCompetitorsCompetitorsPanelSignature> {
  @service declare access: Services['access'];

  @tracked mapIsAvailable = false;

  get activeTab(): CompetitorsBenchmarkTab | undefined {
    const firstAvailableTab = this.visibleTabIds[0];

    return this.visibleTabIds.includes(this.args.activeTab)
      ? this.args.activeTab
      : firstAvailableTab;
  }

  get isSmartCompsetTabSelected(): boolean {
    return this.args.activeTab === BENI_COMPETITORS_TABS.SMART_COMPSET;
  }

  get visibleTabIds(): CompetitorsBenchmarkTab[] {
    const visibleTabs: CompetitorsBenchmarkTab[] = [];

    if (this.access.hasCompsetBenchmark) {
      visibleTabs.push(BENI_COMPETITORS_TABS.COMPSET);
    }

    if (this.access.hasSettingsBenchmarkPortfolioCompsetView) {
      visibleTabs.push(BENI_COMPETITORS_TABS.PORTFOLIO_COMPSET);
    }

    if (this.access.hasCompsetBenchmarkSmartCompset) {
      visibleTabs.push(BENI_COMPETITORS_TABS.SMART_COMPSET);
    }

    return visibleTabs;
  }

  constructor(
    owner: Owner,
    args: HotelBenchmarkInsightCompetitorsCompetitorsPanelSignature['Args'],
  ) {
    super(owner, args);
    this.setMapAvailable();
  }

  setMapAvailable(): void {
    try {
      if (macroCondition(isTesting())) {
        this.mapIsAvailable = false; // Disable maps rendering for visual tests
      } else if (google && google.maps) {
        this.mapIsAvailable = true;
      }
    } catch {
      this.mapIsAvailable = false;
    }
  }

  <template>
    <PrismCard @contentLayout="noPadding" class="{{styles.container}} h-full">
      <:content>
        {{! smart compset builder modal }}
        {{yield}}

        <div
          class="{{styles.content}}
            {{if this.isSmartCompsetTabSelected styles.content--full-width}}"
        >
          {{#if this.activeTab}}
            <HotelBenchmarkInsightCompetitorsCompetitorsListContainer
              @activeTab={{this.activeTab}}
              @compset={{@compset}}
              @isLoading={{@isLoading}}
              @smartCompset={{@smartCompset}}
            />
          {{/if}}
          {{#unless this.isSmartCompsetTabSelected}}
            {{#if this.mapIsAvailable}}
              <HotelBenchmarkInsightCompetitorsMapCompetitorsMap
                @compset={{@compset}}
              />
            {{else}}
              <div
                class="{{styles.map-not-available}}
                  flex items-center justify-center"
              >
                {{#unless @isLoading}}
                  <p class="text-300--regular">
                    {{t "settings.competitors.maps_unavailable"}}
                  </p>
                {{/unless}}
              </div>
            {{/if}}
          {{/unless}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
